import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Activity,
    CheckCircle,
    AlertTriangle,
    XCircle,
    RefreshCw,
    Database,
    Mail,
    Trash2,
    Clock,
    AlertCircle,
    Shield
} from 'lucide-react';
import { apiClient, SystemHealthResponse, ManualJobResponse } from '../services/api';

interface SystemHealthProps {
    userRole: string;
}

const SystemHealth: React.FC<SystemHealthProps> = ({ userRole }) => {
    const [healthData, setHealthData] = useState<SystemHealthResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [jobLoading, setJobLoading] = useState<{ [key: string]: boolean }>({});

    const [countdown, setCountdown] = useState<string>('');

    useEffect(() => {
        loadSystemHealth();
        // Auto-refresh every 30 seconds
        const interval = setInterval(loadSystemHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    // Separate effect for live countdown (updates every second)
    useEffect(() => {
        const updateCountdown = () => {
            setCountdown(getNextEmailRetryCountdown());
        };
        
        // Update immediately
        updateCountdown();
        
        // Update every second for live countdown
        const countdownInterval = setInterval(updateCountdown, 1000);
        
        return () => clearInterval(countdownInterval);
    }, []);

    const loadSystemHealth = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [health] = await Promise.all([
                apiClient.getSystemHealth(),
                apiClient.getJobStats()
            ]);

            setHealthData(health);
            setLastRefresh(new Date());
        } catch (err: any) {
            setError(err.message || 'Failed to load system health');
            console.error('System health error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const triggerManualJob = async (jobType: 'expiry' | 'email' | 'cleanup') => {
        if (userRole !== 'admin' && userRole !== 'manager') {
            toast.error('Insufficient permissions');
            return;
        }

        setJobLoading({ ...jobLoading, [jobType]: true });

        try {
            let response: ManualJobResponse;

            switch (jobType) {
                case 'expiry':
                    response = await apiClient.triggerExpiryCheck();
                    break;
                case 'email':
                    response = await apiClient.triggerEmailRetry();
                    break;
                case 'cleanup':
                    response = await apiClient.triggerTokenCleanup();
                    break;
                default:
                    throw new Error('Invalid job type');
            }

            toast.success(response.message);
            
            // Refresh health data after triggering job
            setTimeout(loadSystemHealth, 2000);
            
        } catch (err: any) {
            toast.error(`Error triggering ${jobType} job: ${err.message}`);
        } finally {
            setJobLoading({ ...jobLoading, [jobType]: false });
        }
    };

    const getNextEmailRetryCountdown = () => {
        const now = new Date();
        const currentHour = now.getUTCHours();
        const currentMinute = now.getUTCMinutes();
        const currentSecond = now.getUTCSeconds();
        
        // Email retry runs every 4 hours: 0, 4, 8, 12, 16, 20
        const retryHours = [0, 4, 8, 12, 16, 20];
        
        // Find the next retry hour
        let nextRetryHour = retryHours.find(hour => hour > currentHour);
        if (!nextRetryHour) {
            nextRetryHour = retryHours[0] + 24; // Next day's first retry
        }
        
        // Calculate time until next retry
        let hoursUntil = nextRetryHour - currentHour;
        let minutesUntil = 0 - currentMinute;
        let secondsUntil = 0 - currentSecond;
        
        if (secondsUntil < 0) {
            secondsUntil += 60;
            minutesUntil -= 1;
        }
        
        if (minutesUntil < 0) {
            minutesUntil += 60;
            hoursUntil -= 1;
        }
        
        // Format the countdown
        if (hoursUntil > 0) {
            return `${hoursUntil}h ${minutesUntil}m`;
        } else if (minutesUntil > 0) {
            return `${minutesUntil}m ${secondsUntil}s`;
        } else {
            return `${secondsUntil}s`;
        }
    };

    const getHealthIcon = (health: string) => {
        switch (health) {
            case 'healthy':
                return <CheckCircle className="w-5 h-5" style={{ color: '#69BE28' }} />;
            case 'degraded':
                return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
            case 'unhealthy':
                return <XCircle className="w-5 h-5 text-red-600" />;
            default:
                return <AlertCircle className="w-5 h-5 text-gray-600" />;
        }
    };

    const getHealthColor = (health: string) => {
        switch (health) {
            case 'healthy':
                return 'border-green-200';
            case 'degraded':
                return 'text-yellow-800 bg-yellow-50 border-yellow-200';
            case 'unhealthy':
                return 'text-red-800 bg-red-50 border-red-200';
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (isLoading && !healthData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="text-gray-600">Loading system health...</span>
                </div>
            </div>
        );
    }

    if (error && !healthData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading System Health</h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={loadSystemHealth}
                        className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* Print Header - Hidden on screen, visible when printing */}
            <div className="hidden print:block print:mb-8">
                <div className="flex items-center justify-between border-b-2 border-black pb-4">
                    <div className="flex items-center">
                        <img 
                            src="/logo-horizontal.png" 
                            alt="IntelliDrive" 
                            className="h-8 w-auto mr-4"
                        />
                        <div>
                            <h1 className="text-xl font-bold text-black">IntelliDrive Platform</h1>
                            <p className="text-sm text-gray-600">Consent Management Report</p>
                        </div>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                        <p>Generated: {new Date().toLocaleDateString()}</p>
                        <p>Page: Consent Management</p>
                    </div>
                </div>
            </div>
            
            {/* System Health Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="w-1/2 sm:flex-1">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">System Health</h2>
                    </div>
                    <button
                        onClick={loadSystemHealth}
                        disabled={isLoading}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md disabled:opacity-50 flex-shrink-0 ml-2"
                        title="Refresh Health Status"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} style={{ color: '#69BE28' }} />
                    </button>
                </div>
                <p className="text-sm sm:text-base text-gray-600 w-3/4 sm:w-full">
                    Monitor background jobs and system status
                </p>
                {healthData && (
                    <span className="text-xs sm:text-sm text-gray-500">
                        Last updated: {formatTime(lastRefresh.toISOString())}
                    </span>
                )}
            </div>

            {healthData && (
                <>
                    {/* Overall Health Status */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Overall Health */}
                        <div className={`rounded-lg border p-6 ${getHealthColor(healthData.data.overallHealth)}`}
                            style={healthData.data.overallHealth === 'healthy' ? {
                                color: '#69BE28',
                                backgroundColor: 'rgba(105, 190, 40, 0.1)'
                            } : undefined}>
                            <div className="flex items-center">
                                <div className="mr-4">
                                    {getHealthIcon(healthData.data.overallHealth)}
                                </div>
                                <div>
                                    <p className="text-sm font-medium">Overall Health</p>
                                    <p className="text-lg font-bold capitalize">{healthData.data.overallHealth}</p>
                                </div>
                            </div>
                        </div>

                        {/* Redis Health */}
                        <div className={`rounded-lg border p-6 ${getHealthColor(healthData.data.redisHealth)}`}
                            style={healthData.data.redisHealth === 'healthy' ? {
                                color: '#69BE28',
                                backgroundColor: 'rgba(105, 190, 40, 0.1)'
                            } : undefined}>
                            <div className="flex items-center">
                                <div className="mr-4">
                                    <Database className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">Redis Health</p>
                                    <p className="text-lg font-bold capitalize">{healthData.data.redisHealth}</p>
                                </div>
                            </div>
                        </div>

                        {/* Background Jobs */}
                        <div className={`rounded-lg border p-6 ${healthData.data.backgroundJobsEnabled ? 'border-green-200' : 'text-red-800 bg-red-50 border-red-200'}`}
                            style={healthData.data.backgroundJobsEnabled ? {
                                color: '#69BE28',
                                backgroundColor: 'rgba(105, 190, 40, 0.1)'
                            } : undefined}>
                            <div className="flex items-center">
                                <div className="mr-4">
                                    <Activity className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">Background Jobs</p>
                                    <p className="text-lg font-bold">
                                        {healthData.data.backgroundJobsEnabled ? 'Enabled' : 'Disabled'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Queue Statistics */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Queue Statistics</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Consent Queue */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <Shield className="w-5 h-5 mr-2" style={{ color: '#69BE28' }} />
                                    <h4 className="font-medium text-gray-900">Consent Queue</h4>
                                </div>
                                <div className="space-y-2 mb-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Waiting:</span>
                                        <span className="font-medium">{healthData.data.queueStats.consent.waiting}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Active:</span>
                                        <span className="font-medium">{healthData.data.queueStats.consent.active}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Completed:</span>
                                        <span className="font-medium" style={{ color: '#69BE28' }}>{healthData.data.queueStats.consent.completed}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Failed:</span>
                                        <span className="font-medium text-red-600">{healthData.data.queueStats.consent.failed}</span>
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        Checks for expiring driver consents and sends warnings. Runs daily at 6am UTC.
                                    </p>
                                </div>
                            </div>

                            {/* Email Queue */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <Mail className="w-5 h-5 text-green-600 mr-2" />
                                    <h4 className="font-medium text-gray-900">Email Queue</h4>
                                </div>
                                <div className="space-y-2 mb-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Waiting:</span>
                                        <span className="font-medium">{healthData.data.queueStats.email.waiting}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Active:</span>
                                        <span className="font-medium">{healthData.data.queueStats.email.active}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Completed:</span>
                                        <span className="font-medium text-green-600">{healthData.data.queueStats.email.completed}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Failed:</span>
                                        <span className="font-medium text-red-600">{healthData.data.queueStats.email.failed}</span>
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        System-wide email retry job (not organization-specific). Retries failed emails every 4 hours.
                                    </p>
                                </div>
                            </div>

                            {/* Cleanup Queue */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center mb-3">
                                    <Trash2 className="w-5 h-5 text-purple-600 mr-2" />
                                    <h4 className="font-medium text-gray-900">Cleanup Queue</h4>
                                </div>
                                <div className="space-y-2 mb-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Waiting:</span>
                                        <span className="font-medium">{healthData.data.queueStats.cleanup.waiting}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Active:</span>
                                        <span className="font-medium">{healthData.data.queueStats.cleanup.active}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Completed:</span>
                                        <span className="font-medium text-green-600">{healthData.data.queueStats.cleanup.completed}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Failed:</span>
                                        <span className="font-medium text-red-600">{healthData.data.queueStats.cleanup.failed}</span>
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        System-wide maintenance job (not organization-specific). Cleans expired tokens daily at 2am UTC.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Manual Job Controls */}
                    {(userRole === 'admin' || userRole === 'manager') && (
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Manual Job Controls</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Expiry Check Button */}
                                <div className="text-center">
                                    <button
                                        onClick={() => triggerManualJob('expiry')}
                                        disabled={jobLoading.expiry}
                                        className="w-full flex items-center justify-center px-4 py-3 border rounded-md shadow-sm text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                                        style={{ 
                                            backgroundColor: 'rgba(105, 190, 40, 0.1)', 
                                            color: '#69BE28', 
                                            borderColor: 'rgba(105, 190, 40, 0.3)' 
                                        }}
                                    >
                                        {jobLoading.expiry ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Clock className="w-4 h-4 mr-2" />
                                        )}
                                        Trigger Expiry Check
                                    </button>
                                    <p className="text-xs text-gray-500 mt-1">runs daily at 6am</p>
                                </div>

                                {/* Email Retry Button */}
                                <div className="text-center">
                                    <button
                                        onClick={() => triggerManualJob('email')}
                                        disabled={jobLoading.email}
                                        className="w-full flex items-center justify-center px-4 py-3 border rounded-md shadow-sm text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                                        style={{ 
                                            backgroundColor: 'rgba(105, 190, 40, 0.1)', 
                                            color: '#69BE28', 
                                            borderColor: 'rgba(105, 190, 40, 0.3)' 
                                        }}                                    >
                                        {jobLoading.email ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Mail className="w-4 h-4 mr-2" />
                                        )}
                                        Retry Failed Emails
                                    </button>
                                    <p className="text-xs text-gray-500 mt-1">
                                        runs every 4 hours
                                        <br />
                                        <span className="font-medium" style={{ color: '#69BE28' }}>next in {countdown}</span>
                                    </p>
                                </div>

                                {/* Token Cleanup Button */}
                                <div className="text-center">
                                    <button
                                        onClick={() => triggerManualJob('cleanup')}
                                        disabled={jobLoading.cleanup}
                                        className="w-full flex items-center justify-center px-4 py-3 border border-purple-300 rounded-md shadow-sm bg-purple-50 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                                    >
                                        {jobLoading.cleanup ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4 mr-2" />
                                        )}
                                        Clean Up Tokens
                                    </button>
                                    <p className="text-xs text-gray-500 mt-1">runs daily at 2am</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Issues Alert */}
                    {healthData.data.issues.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                            <div className="flex items-center mb-3">
                                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                                <h3 className="text-lg font-medium text-red-900">System Issues Detected</h3>
                            </div>
                            <ul className="space-y-1">
                                {healthData.data.issues.map((issue, index) => (
                                    <li key={index} className="text-sm text-red-800">• {issue}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SystemHealth;